// src/components/BotsPage.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  SmartToy as BotIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiFetch from '../utils/api';

function BotsPage() {
  const navigate = useNavigate();
  const { blestaLinked, blestaServices } = useAuth();
  const [ownedBots, setOwnedBots] = useState([]);
  const [managedBots, setManagedBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [newBotToken, setNewBotToken] = useState('');
  const [newBotPrefix, setNewBotPrefix] = useState('!');
  const [selectedNamespace, setSelectedNamespace] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchBots();
  }, []);

  const fetchBots = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/bots', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setOwnedBots(data.owned || []);
        setManagedBots(data.managed || []);
      } else {
        throw new Error('Failed to fetch bots');
      }
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const activeServices = (blestaServices || []).filter((s) => s.status === 'active');

  const handleCreateBot = async () => {
    if (!newBotName.trim()) {
      setError('Bot name is required');
      return;
    }
    if (!newBotToken.trim() || newBotToken.trim().length < 50) {
      setError('A valid Discord bot token is required');
      return;
    }
    if (!selectedNamespace) {
      setError('Please select a namespace');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/bots/${selectedNamespace}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: newBotName.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-'),
          discord_token: newBotToken.trim(),
          env_vars: {
            BOT_PREFIX: newBotPrefix || '!',
          },
        }),
      });

      if (response.ok) {
        const newBot = await response.json();
        setOwnedBots([newBot, ...ownedBots]);
        setCreateDialogOpen(false);
        setNewBotName('');
        setNewBotToken('');
        setNewBotPrefix('!');
        setSelectedNamespace('');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create bot');
      }
    } catch (err) {
      console.error('Error creating bot:', err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleBotAction = async (namespace, botName, action) => {
    try {
      const response = await apiFetch(`/api/bots/${namespace}/${botName}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const updatedBot = await response.json();
        setOwnedBots(ownedBots.map((bot) =>
          bot.namespace === namespace && bot.name === botName ? { ...bot, ...updatedBot } : bot
        ));
        setManagedBots(managedBots.map((bot) =>
          bot.namespace === namespace && bot.name === botName ? { ...bot, ...updatedBot } : bot
        ));
      } else {
        throw new Error(`Failed to ${action} bot`);
      }
    } catch (err) {
      console.error(`Error ${action}ing bot:`, err);
      setError(err.message);
    }
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case 'running': return '#66bb6a';
      case 'stopped': return '#9e9e9e';
      case 'pending': case 'creating': return '#42a5f5';
      case 'error': return '#ef5350';
      default: return '#9e9e9e';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group owned bots by namespace
  const botsByNamespace = {};
  ownedBots.forEach((bot) => {
    const ns = bot.namespace || 'default';
    if (!botsByNamespace[ns]) {
      botsByNamespace[ns] = [];
    }
    botsByNamespace[ns].push(bot);
  });

  const renderBotRow = (bot, showRole) => {
    const canOperate = !showRole || bot.role === 'operator' || bot.role === 'manager';

    return (
      <TableRow key={`${bot.namespace}-${bot.name}`}>
        <TableCell>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {bot.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.7rem' }}>
              {bot.namespace}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            icon={<DotIcon sx={{ fontSize: '10px !important', color: `${getStatusDotColor(bot.status)} !important` }} />}
            label={bot.status}
            size="small"
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.1)', height: 26 }}
          />
        </TableCell>
        {showRole && (
          <TableCell>
            <Chip label={bot.role} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)', height: 24, fontSize: '0.75rem' }} />
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
            {formatDate(bot.createdAt)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <Tooltip title="Configure">
              <IconButton
                size="small"
                onClick={() => navigate(`/bots/${bot.namespace}/${bot.name}`)}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {canOperate && (
              <>
                {bot.status === 'stopped' || bot.status === 'creating' || bot.status === 'pending' ? (
                  <Tooltip title="Start">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleBotAction(bot.namespace, bot.name, 'start')}
                    >
                      <StartIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Stop">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleBotAction(bot.namespace, bot.name, 'stop')}
                    >
                      <StopIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Restart">
                  <IconButton
                    size="small"
                    onClick={() => handleBotAction(bot.namespace, bot.name, 'restart')}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasNoBots = ownedBots.length === 0 && managedBots.length === 0;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              My Bots
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Create, configure, and manage your modmail bot instances.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={activeServices.length === 0}
          >
            Create Bot
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeServices.length === 0 && blestaLinked === false && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Link your Blesta account to manage bots. Visit the Account page to get started.
        </Alert>
      )}

      {activeServices.length === 0 && blestaLinked === true && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No active services found. Purchase a bot hosting service to create bots.
        </Alert>
      )}

      {hasNoBots ? (
        <Card sx={{ border: '1px dashed rgba(255,255,255,0.12)' }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <BotIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.4 }} />
            <Typography variant="h6" gutterBottom>
              No bots yet
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Create your first modmail bot to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={activeServices.length === 0}
            >
              Create Bot
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Owned Bots - grouped by namespace */}
          {ownedBots.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                Owned Bots
              </Typography>
              {Object.entries(botsByNamespace).map(([ns, bots]) => (
                <Box key={ns} sx={{ mb: 3 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.1em' }}>
                    {ns}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'divider', mt: 0.5 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bots.map((bot) => renderBotRow(bot, false))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}

          {/* Managed Bots */}
          {managedBots.length > 0 && (
            <Box>
              {ownedBots.length > 0 && <Divider sx={{ mb: 3 }} />}
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                Managed Bots
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {managedBots.map((bot) => renderBotRow(bot, true))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}

      {/* Create Bot Dialog */}
      <Dialog open={createDialogOpen} onClose={() => !creating && setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Create New Bot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Set up a new modmail bot instance. You'll need a Discord bot token from the Developer Portal.
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Namespace (Service)</InputLabel>
            <Select
              value={selectedNamespace}
              onChange={(e) => setSelectedNamespace(e.target.value)}
              label="Namespace (Service)"
              disabled={creating}
            >
              {activeServices.map((service) => (
                <MenuItem key={service.serviceId || service.namespace} value={service.namespace}>
                  {service.namespace} ({service.botSlots} slots)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Bot Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newBotName}
            onChange={(e) => setNewBotName(e.target.value)}
            disabled={creating}
            helperText="Lowercase letters, numbers, and hyphens only"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Discord Bot Token"
            type="password"
            fullWidth
            variant="outlined"
            value={newBotToken}
            onChange={(e) => setNewBotToken(e.target.value)}
            disabled={creating}
            helperText="From the Discord Developer Portal (Bot section)"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Command Prefix"
            type="text"
            fullWidth
            variant="outlined"
            value={newBotPrefix}
            onChange={(e) => setNewBotPrefix(e.target.value)}
            disabled={creating}
            helperText="The prefix used for bot commands (e.g., !, ?, $)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateBot}
            variant="contained"
            disabled={creating || !newBotName.trim() || !newBotToken.trim() || !selectedNamespace}
          >
            {creating ? <CircularProgress size={24} /> : 'Create Bot'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BotsPage;
